import { useState } from "react";

import {
  capLabel,
  tierLimitOf,
  toolCountOf,
  type McpQuotaPatch,
  type McpQuotaTier,
} from "../../api/mcp";
import type { AppConfig } from "../../config";
import { Button, Input, Select } from "../../f7";
import { useWrite } from "../../hooks/useWrite";
import { Gate } from "../authority/Gate";
import { Badge, Card } from "../ui";
import { ConfirmDialog } from "../write/ConfirmDialog";
import { effectFromSeconds, WriteResult } from "../write/EffectTiming";
import { WriteFailure } from "../write/WriteFailure";

/** The two tier-wide keys `PATCH /admin/quota/{tier}` replaces. `null` removes. */
const LIMITS = [
  { key: "tool_default" as const, meaning: "the daily cap for any tool without its own" },
  { key: "max_calls_per_day" as const, meaning: "the daily total across every tool" },
];

type LimitKey = (typeof LIMITS)[number]["key"];
type Mode = "" | "unlimited" | "gated" | "metered" | "remove";

/**
 * Edit a tier's `tool_default` and `max_calls_per_day`.
 *
 * These two stopped being editable with the per-tool cap list on purpose:
 * setting either to 0 gates every tool on the tier, which is a different act
 * from capping one tool. They are written here, one field at a time, on
 * `PATCH /admin/quota/{tier}`.
 */
export function TierDefaults({
  config,
  tier,
  quota,
  onDone,
}: {
  config: AppConfig;
  tier: string;
  quota: McpQuotaTier;
  onDone: () => void;
}) {
  const write = useWrite<McpQuotaPatch>(config);
  const [modes, setModes] = useState<Record<LimitKey, Mode>>({
    tool_default: "",
    max_calls_per_day: "",
  });
  const [perDay, setPerDay] = useState<Record<LimitKey, string>>({
    tool_default: "",
    max_calls_per_day: "",
  });
  const [pending, setPending] = useState<{ key: LimitKey; value: number | null } | null>(null);
  /** The change a 409 refused, kept so a deliberate force resends it. */
  const [attempt, setAttempt] = useState<{ key: LimitKey; value: number | null } | null>(null);
  const [forcing, setForcing] = useState(false);
  const [result, setResult] = useState<McpQuotaPatch | null>(null);
  const busy = write.state.kind === "submitting";
  const toolCount = write.state.kind === "guardRefused" ? toolCountOf(write.state.error.body) : undefined;

  function chosen(key: LimitKey): number | null | undefined {
    const mode = modes[key];
    // null removes the key. -1 would store "unlimited" instead of deleting it.
    if (mode === "remove") return null;
    if (mode === "unlimited") return -1;
    if (mode === "gated") return 0;
    if (mode !== "metered") return undefined;
    const n = Number(perDay[key].trim());
    return Number.isInteger(n) && n >= 1 ? n : undefined;
  }

  function submit(reason: string, force = false) {
    const change = force ? attempt : pending;
    if (!change) return;
    setAttempt(change);
    const body: Record<string, unknown> = { [change.key]: change.value };
    if (reason) body.reason = reason;
    void write
      .submit(`/mcp/admin/quota/${encodeURIComponent(tier)}`, {
        method: "PATCH",
        // `force` is a query param. A body field would 422 (`extra=forbid`)
        // and would not override the guard.
        ...(force ? { query: { force: true } } : {}),
        body: JSON.stringify(body),
      })
      .then((r) => {
        if (!r) return;
        setResult(r);
        setAttempt(null);
        setModes({ tool_default: "", max_calls_per_day: "" });
        onDone();
      });
    setPending(null);
    setForcing(false);
  }

  return (
    <Card title="Tier defaults" note="What every tool inherits, and the daily total across all of them.">
      <div className="flex flex-col gap-4">
        {LIMITS.map(({ key, meaning }) => {
          const applied = capLabel(tierLimitOf(quota, key));
          const stored = typeof quota.config?.[key] === "number";
          const value = chosen(key);
          const ready = value !== undefined;
          return (
            <div key={key} className="flex flex-col gap-2">
              <p className="text-sm">
                <span className="ref">{key}</span>
                <span className="ml-2 text-xs text-slate-500 dark:text-slate-400">{meaning}</span>
              </p>
              <p className="text-xs text-slate-500 dark:text-slate-400">
                Applied <Badge tone={applied.tone}>{applied.text}</Badge>
                <span className="ml-2">{stored ? "this tier" : "not set — unlimited"}</span>
              </p>
              <div className="flex flex-wrap items-end gap-2">
                <Select
                  aria-label={key}
                  value={modes[key]}
                  onChange={(e) =>
                    setModes((m) => ({ ...m, [key]: e.target.value as Mode }))
                  }
                  disabled={busy}
                >
                  <option value="">Change…</option>
                  <option value="unlimited">Unlimited</option>
                  <option value="gated">Gated</option>
                    <option value="metered">Metered</option>
                    {stored && <option value="remove">Remove</option>}
                  </Select>
                {modes[key] === "metered" && (
                  <div className="w-28">
                    <Input
                      aria-label={`Daily cap for ${key}`}
                      value={perDay[key]}
                      inputMode="numeric"
                      disabled={busy}
                      onChange={(e) => setPerDay((p) => ({ ...p, [key]: e.target.value }))}
                    />
                  </div>
                )}
                <Gate need="quota:write">
                  <Button
                    disabled={busy || !ready}
                    onClick={() => ready && setPending({ key, value })}
                  >
                    Save {key}
                  </Button>
                </Gate>
              </div>
            </div>
          );
        })}
      </div>

      {result && (
        <div className="mt-3">
          <WriteResult title="Tier defaults updated" onDismiss={() => setResult(null)}>
            <p className="text-sm">{effectFromSeconds(result.effective_within_seconds, "This")}</p>
          </WriteResult>
        </div>
      )}
      <WriteFailure state={write.state} onDismiss={write.reset} onReread={onDone} />
      {toolCount !== undefined && attempt && (
        <div className="mt-2">
          <p className="text-sm">
            This gates {toolCount} tools. Forcing it needs <span className="ref">quota:force</span>.
          </p>
          <div className="mt-2">
            <Gate need="quota:force">
              <Button disabled={busy} onClick={() => setForcing(true)}>
                Force this change
              </Button>
            </Gate>
          </div>
        </div>
      )}

      <ConfirmDialog
        open={pending !== null}
        title={pending ? `Set ${pending.key} on ${tier}` : "Set a tier default"}
        confirmLabel="Save default"
        requireReason
        reasonHint="why this default, e.g. DA-9"
        busy={busy}
        onConfirm={submit}
        onCancel={() => setPending(null)}
        effect={effectFromSeconds(quota.effective_within_seconds, "This")}
      >
        {pending && (
          <p>
            <span className="ref">{pending.key}</span> on <span className="ref">{tier}</span>{" "}
            {pending.value === null ? (
              "is removed, so the tier falls back to unlimited."
            ) : (
              <>
                becomes <span className="tabular-nums">{capLabel(pending.value).text}</span>.
              </>
            )}
          </p>
        )}
      </ConfirmDialog>

      <ConfirmDialog
        open={forcing}
        title={attempt ? `Force ${attempt.key} on ${tier}` : "Force this change"}
        confirmLabel="Force"
        requireReason
        destructive
        reasonHint="why this default, e.g. DA-9"
        busy={busy}
        onConfirm={(reason) => submit(reason, true)}
        onCancel={() => setForcing(false)}
        effect={effectFromSeconds(quota.effective_within_seconds, "This")}
      >
        <p>
          This gates {toolCount ?? "the"} tools for every merchant on <span className="ref">{tier}</span>.
          Force does not skip a version conflict.
        </p>
      </ConfirmDialog>
    </Card>
  );
}
